// Default images/icons for itinerary / operation items that have no own photo.
// Mirrors legacy `getCarTypeImagePath` + `ContentCategoryImage` (trip_planner):
// car-type PNG for transport, type ICONS for Ticket/RentalCar/Guide, and the
// category PNG for everything else. PNG assets live in
// apps/web/public/{carType,category}.

// transportType value → /carType file stem. Keys match the legacy
// `TransportationCarType` enum values. "TukTuk" has no asset (legacy parity).
const CAR_TYPE_FILE: Record<string, string> = {
  Sedan: 'sedan',
  Minivan: 'minivan',
  Minibus: 'minibus',
  Bus: 'bus',
  SUV: 'suv',
  Sprinter: 'sprinter',
  'Sedan class E': 'sedan_class_e',
  'Sedan class S': 'sedan_class_s',
};

const CATEGORY_FILES = new Set([
  'accommodation',
  'activity',
  'attraction',
  'destination',
  'dining',
  'information',
  'other',
  'personnel',
  'transportation',
  'venue',
]);

export const carTypeImageUrl = (
  transportType: string | null | undefined
): string | null => {
  const file = transportType ? CAR_TYPE_FILE[transportType] : undefined;
  return file ? `/carType/${file}.png` : null;
};

export const categoryImageUrl = (
  category: string | null | undefined
): string | null => {
  const key = category?.toLowerCase();
  return key && CATEGORY_FILES.has(key) ? `/category/${key}.png` : null;
};

// The legacy `getSubitemIcon` types. Ticket/RentalCar always; Guide only when
// the row is a sub-item.
export type ItemFallbackIcon = 'ticket' | 'car' | 'guide';

// What to show when an item has no own photo: a PNG image (car-type / category)
// or a type icon (Ticket/RentalCar/Guide), or null when nothing maps.
export type ItemImageFallback =
  | { kind: 'image'; src: string }
  | { kind: 'icon'; icon: ItemFallbackIcon };

export const fallbackItemImage = (item: {
  contentCategory: string | null;
  contentSubcategory?: string | null;
  transportType?: string | null;
  parentId?: string | null;
}): ItemImageFallback | null => {
  // Transportation car-type image wins (legacy resolves `image` = carType
  // before the ContentCategoryImage fallback).
  if (item.contentCategory === 'Transportation') {
    const car = carTypeImageUrl(item.transportType);
    if (car) return { kind: 'image', src: car };
  }

  // Type icons (legacy getSubitemIcon): Ticket/RentalCar always, Guide only
  // for sub-items.
  const sub = item.contentSubcategory;
  if (sub === 'Ticket') return { kind: 'icon', icon: 'ticket' };
  if (sub === 'RentalCar') return { kind: 'icon', icon: 'car' };
  if (sub === 'Guide' && item.parentId != null) {
    return { kind: 'icon', icon: 'guide' };
  }

  const cat = categoryImageUrl(item.contentCategory);
  if (cat) return { kind: 'image', src: cat };
  return null;
};
